import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ClearAll as ClearCacheIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import CrearUsuarioWordPress from '../../components/forms/CrearUsuarioWordPress';

interface CommunityUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  roles: string[];
  registered: string;
  documento: {
    tipo: string;
    numero: string;
  };
  meta: {
    telefono?: string;
  };
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_pages: number;
  total_users: number;
  has_next: boolean;
  has_prev: boolean;
}

const UsuariosComunidad: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<CommunityUser[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 25,
    total_pages: 0,
    total_users: 0,
    has_next: false,
    has_prev: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filtros
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // Modal de creación
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Cargar usuarios
  const loadUsers = async (page: number = 1, role?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '25'
      });
      
      if (role) {
        params.append('role_filter', role);
      }

      const response = await fetch(`/api/wordpress/community-users?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setUsers(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.message || 'Error cargando usuarios');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Error de conexión al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  // Limpiar caché
  const handleFlushCache = async () => {
    try {
      const response = await fetch('/api/wordpress/community-users/cache', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Caché limpiado exitosamente. ${data.keys_cleared} claves eliminadas.`);
        // Recargar datos
        loadUsers(pagination.current_page, roleFilter);
      } else {
        setError(data.message || 'Error limpiando caché');
      }
    } catch (error) {
      console.error('Error flushing cache:', error);
      setError('Error de conexión al limpiar caché');
    }
  };

  // Exportar usuarios
  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const params = new URLSearchParams({
        format
      });
      
      if (roleFilter) {
        params.append('role_filter', roleFilter);
      }

      const response = await fetch(`/api/wordpress/community-users/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `usuarios_comunidad_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xls' : 'csv'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setSuccess(`Exportación ${format.toUpperCase()} completada exitosamente`);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error en la exportación');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      setError('Error de conexión durante la exportación');
    }
  };

  // Filtrar usuarios localmente por término de búsqueda
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      user.display_name.toLowerCase().includes(searchLower) ||
      user.documento?.numero?.includes(searchTerm)
    );
  });

  // Efectos
  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handlers
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    loadUsers(page, roleFilter);
  };

  const handleRoleFilterChange = (event: any) => {
    const newRole = event.target.value;
    setRoleFilter(newRole);
    loadUsers(1, newRole); // Reset to page 1 when filtering
  };

  const handleRefresh = () => {
    loadUsers(pagination.current_page, roleFilter);
  };

  const getRoleColor = (roles: string[]) => {
    if (roles.includes('comunidad_premium')) return 'primary';
    if (roles.includes('comunidad_estandar')) return 'secondary';
    return 'default';
  };

  const getRoleLabel = (roles: string[]) => {
    if (roles.includes('comunidad_premium')) return 'Premium';
    if (roles.includes('comunidad_estandar')) return 'Estándar';
    return roles.join(', ');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-PE');
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Usuarios de la Comunidad
      </Typography>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Controles superiores */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Buscar usuarios"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              placeholder="Email, nombre, documento..."
              sx={{ minWidth: 300 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filtrar por rol</InputLabel>
              <Select
                value={roleFilter}
                label="Filtrar por rol"
                onChange={handleRoleFilterChange}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="comunidad_estandar">Estándar</MenuItem>
                <MenuItem value="comunidad_premium">Premium</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
              >
                Crear Usuario
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Actualizar
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('csv')}
              >
                CSV
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleExport('excel')}
              >
                Excel
              </Button>
              
              <Tooltip title="Limpiar caché para obtener datos actualizados">
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<ClearCacheIcon />}
                  onClick={handleFlushCache}
                >
                  Limpiar Caché
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Estadísticas */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" color="primary">
                {pagination.total_users}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Usuarios
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" color="secondary">
                {filteredUsers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mostrando
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6">
                {pagination.current_page}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Página Actual
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6">
                {pagination.total_pages}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Páginas
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabla de usuarios */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Registro</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {user.display_name || `${user.first_name} ${user.last_name}`.trim()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{user.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(user.roles)}
                            color={getRoleColor(user.roles)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.documento?.tipo && user.documento?.numero ? (
                            <Box>
                              <Typography variant="body2">
                                {user.documento.tipo.toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {user.documento.numero}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No registrado
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.meta?.telefono || (
                            <Typography variant="body2" color="text.secondary">
                              No registrado
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDate(user.registered)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              {pagination.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={pagination.total_pages}
                    page={pagination.current_page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de creación de usuario */}
      <CrearUsuarioWordPress
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(message) => {
          setSuccess(message);
          // Recargar usuarios después de crear uno nuevo
          loadUsers(pagination.current_page, roleFilter);
        }}
        onError={(message) => setError(message)}
      />
      </Box>
    </Layout>
  );
};

export default UsuariosComunidad;
